import type React from "react";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import toast from "react-hot-toast";

import { type InfiniteData, useQueryClient } from "@tanstack/react-query";

import type { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button, type ButtonProps, Loader, Popup } from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";

import IdeaProjectEventItem from "./IdeaProjectEventItem";
import { type IProjectIdeaEvent, hasEventType } from "./ideaEventUtils";
import {
  getBoardEventsQueryKey,
  updateBoardEventInCache,
  useBoardEventsInfiniteQuery,
} from "./useBoardEventsInfiniteQuery";

type BoardEventsPage = OpenAPI.GET<"/boards/{for_id}/events">["response"];

const EventContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 6px 0 0;
  width: 430px;
  max-height: 600px;
  min-height: 100px;
  overflow: auto;
  scrollbar-width: thin;
`;

const BoardEvents = ({
  forId,
  setCurrentIdeaId,
  buttonStyle,
  buttonProps = {},
}: {
  forId: string;
  setCurrentIdeaId: Dispatch<SetStateAction<string>>;
  buttonStyle?: React.CSSProperties;
  buttonProps?: ButtonProps;
}) => {
  const user = useAppSelector((state) => state.user);
  const queryClient = useQueryClient();
  const [seenEvents, setSeenEvents] = useState<boolean>(true);
  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const [challenge, setChallenge] = useState<
    OpenAPI.Schemas["Challenge"] | null
  >(null);
  const bottomOfEventsRef = useRef<HTMLDivElement | null>(null);
  const prevPageCountRef = useRef(0);
  const userId = user?._id;
  const theme = useTheme();
  const defaultLaneName =
    challenge?.projectBoard?.defaultLane?.name ?? "Default";

  const {
    events,
    seen,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    data,
  } = useBoardEventsInfiniteQuery({ forId, limit: 25 });

  useEffect(() => {
    setSeenEvents(seen);
  }, [seen]);

  useEffect(() => {
    if (error) {
      toast.error(error.message);
    }
  }, [error]);

  useEffect(() => {
    const pageCount = data?.pages.length ?? 0;
    if (pageCount > prevPageCountRef.current && pageCount > 1) {
      bottomOfEventsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }
    prevPageCountRef.current = pageCount;
  }, [data?.pages.length]);

  useEffect(() => {
    if (!forId) return;
    api.challenges.get(
      forId,
      (challengeData) => {
        if (challengeData) setChallenge(challengeData);
      },
      () => {},
    );
  }, [forId]);

  const onEventClick = useCallback(
    (event: IProjectIdeaEvent) => {
      setPopupOpen(false);
      setCurrentIdeaId(event.idea);
      api.boards.markBoardEventAsInteracted(
        forId,
        event._id,
        () => {
          queryClient.setQueryData<InfiniteData<BoardEventsPage>>(
            getBoardEventsQueryKey(forId, { limit: 25 }),
            (currentData) =>
              updateBoardEventInCache(currentData, event._id, userId ?? ""),
          );
        },
        () => {},
      );
    },
    [forId, queryClient, setCurrentIdeaId, userId],
  );

  const onOpen = useCallback(() => {
    setPopupOpen(true);
    api.boards.markBoardEventsAsSeen(
      forId,
      () => setSeenEvents(true),
      () => {},
    );
  }, [forId]);

  if (!events.length && !isLoading) return null;

  return (
    <Popup
      onClose={() => setPopupOpen(false)}
      open={popupOpen}
      on="click"
      wide
      trigger={
        <Button
          color={!seenEvents ? "red" : undefined}
          icon={seenEvents ? "list" : "bell"}
          style={{ ...(buttonStyle ?? {}) }}
          size="small"
          content={theme.sizes.isLargeComputer ? "Recent activity" : null}
          {...buttonProps}
        />
      }
      onOpen={onOpen}
      position="bottom right"
      content={
        <EventContainer>
          {isLoading ?
            <Loader active style={{ margin: 10 }} />
          : <>
              {events.map((event) => {
                if (!hasEventType(event.action)) return null;
                return (
                  <IdeaProjectEventItem
                    key={event._id}
                    event={event}
                    showIdeaName
                    defaultLaneName={defaultLaneName}
                    onClick={() => onEventClick(event)}
                    interacted={
                      !!userId && !!event.interacted?.includes(userId)
                    }
                  />
                );
              })}
              {isFetchingNextPage ?
                <Loader active style={{ margin: 10 }} />
              : null}
              {hasNextPage && !isFetchingNextPage ?
                <Button
                  size="small"
                  onClick={() => fetchNextPage()}
                  content="Load more"
                  style={{
                    marginTop: 5,
                    width: "99%",
                    minHeight: 30,
                    padding: 0,
                  }}
                />
              : null}
              <div ref={bottomOfEventsRef} />
            </>
          }
        </EventContainer>
      }
    />
  );
};

export default BoardEvents;
